import { useCallback, useRef } from "react";

interface TtsQueueManagerProps {
  onContentReady: (content: string) => void;
  onContentComplete: () => void;
  onClearContent: () => void;
  onClearCurrentContent: () => void;
}

export const useTtsQueueManager = ({
  onContentReady,
  onContentComplete,
  onClearContent,
  onClearCurrentContent,
}: TtsQueueManagerProps) => {
  const ttsContentQueueRef = useRef<string[]>([]);
  const currentDisplayedContentRef = useRef<string>("");
  const currentPlayingContentRef = useRef<string>("");

  const addToTtsQueue = useCallback((content: string) => {
    ttsContentQueueRef.current.push(content);
  }, []);

  const markTtsAudioReady = useCallback(() => {
    if (ttsContentQueueRef.current.length > 0) {
      const content = ttsContentQueueRef.current[0];

      if (
        content &&
        (currentDisplayedContentRef.current !== content ||
          !currentDisplayedContentRef.current)
      ) {
        currentDisplayedContentRef.current = content;
        currentPlayingContentRef.current = content;
        onContentReady(content);
      } else if (content && currentDisplayedContentRef.current === content) {
        currentPlayingContentRef.current = content;
      }
    }
  }, [onContentReady]);

  const removeItemWhenAudioComplete = useCallback(() => {
    ttsContentQueueRef.current.shift();
    currentPlayingContentRef.current = "";

    if (!ttsContentQueueRef.current.length) {
      onClearContent();
    }
  }, [onClearContent, onClearCurrentContent, onContentReady]);

  const markCurrentContentComplete = useCallback(() => {
    onContentComplete();
  }, [onContentComplete]);

  // Clear the entire queue
  const clearQueue = useCallback(() => {
    ttsContentQueueRef.current = [];
    currentDisplayedContentRef.current = "";
    currentPlayingContentRef.current = "";
  }, []);

  // Get queue status for debugging
  const getQueueStatus = useCallback(() => {
    return {
      totalItems: ttsContentQueueRef.current.length,
      currentDisplayedContent: currentDisplayedContentRef.current,
      queue: ttsContentQueueRef.current.map(
        (content) => content.substring(0, 50) + "..."
      ),
    };
  }, []);

  return {
    ttsContentQueueRef,
    addToTtsQueue,
    markTtsAudioReady,
    markCurrentContentComplete,
    removeItemWhenAudioComplete,
    clearQueue,
    getQueueStatus,
  };
};
